import { FC, useEffect, useLayoutEffect, useRef, useState } from "react";

import { useBlocNext } from "@blac/react";
import styled from "@emotion/styled";
import translate from "src/lib/translate";
import MealInsightsCubit from "src/state/MealInsightsCubit/MealInsightsCubit";
import MealsStatsCubit from "src/state/MealsStatsCubit/MealsStatsCubit";
import { tracker } from "src/state/state";
import { TrackEvent } from "src/state/Track/TrackCubit";
import { IconExpand } from "src/ui/assets/icons/IconExpand";
import Translate from "src/ui/components/Translate/Translate";

import {
  Card,
  Fog,
  Footer,
  Insight,
  InsightWrap,
  Label
} from "./MealTodayInsightCard.styled";
import MealTodayInsightModal from "./MealTodayInsightModal";
import { formatMealTime } from "./mealInsightHelpers";

const ExpandButton = styled.span`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  color: var(--Greys-Gray, #aaa7a0);
  line-height: 0;

  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const MealTodayInsightsCard: FC = () => {
  const [{ todaySummary }] = useBlocNext(MealInsightsCubit);
  const [{ insights }] = useBlocNext(MealsStatsCubit);
  const [modalOpen, setModalOpen] = useState(false);
  const [overflows, setOverflows] = useState(true);
  const insightRef = useRef<HTMLParagraphElement>(null);

  useLayoutEffect(() => {
    const el = insightRef.current;
    if (!el) {
      return;
    }
    const measureOverflow = () => {
      if (el.clientHeight === 0) {
        return;
      }
      setOverflows(el.scrollHeight - el.clientHeight > 1);
    };
    measureOverflow();
    const observer = new ResizeObserver(measureOverflow);
    observer.observe(el);
    return () => observer.disconnect();
  }, [insights]);

  const { mealCount, latestMealAt } = todaySummary;

  useEffect(() => {
    tracker.track(TrackEvent.DailyInsightShortDisplayed);
  }, []);

  const openInsight = (): void => {
    tracker.track(TrackEvent.DailyInsightOpened);
    setModalOpen(true);
  };

  const footerParts = [
    latestMealAt
      ? translate("mealInsights.today.updatedAt", {
          time: formatMealTime(latestMealAt)
        })
      : undefined,
    translate("mealInsights.today.mealsLogged", { count: mealCount })
  ].filter(Boolean);

  const footer = footerParts.join(" · ");

  return (
    <>
      <Card
        type="button"
        onClick={openInsight}
        aria-label={translate("mealInsights.today.expand")}
      >
        <Label>
          <Translate msg="mealInsights.today.insightsLabel" />
        </Label>
        <InsightWrap>
          <Insight ref={insightRef}>{insights}</Insight>
          {overflows && <Fog aria-hidden />}
        </InsightWrap>
        <Footer>{footerParts.join(" · ")}</Footer>
        <ExpandButton aria-hidden>
          <IconExpand />
        </ExpandButton>
      </Card>
      {modalOpen && (
        <MealTodayInsightModal
          insights={insights}
          footer={footer}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
};

export default MealTodayInsightsCard;
